// Fetches the shared CC0 library manifest and registers it with the engine.
//
// This lives in the engine (not a host app) so every consumer - Studio, and any
// game built on build-kit - loads the library the same way. One fetch per
// session; a host boots fine without the library (offline dev, CDN hiccup):
// failure logs a single warning and resolves, leaving the library unregistered
// and every other pack intact.
import { parseLibraryManifest, isLibraryRegistered, registerLibraryPack } from './libraryPack';

let pending: Promise<void> | null = null;
let loadedBase: string | null = null;
// Unlike loadedBase (cleared on failure so a same-base retry re-fetches
// instead of no-op-ing), this is never cleared - it's what lets a UI far
// from the call site (ObjectBrowser doesn't know the library base URL a
// host chose) retry without re-threading that URL through every layer.
let lastAttemptedBase: string | null = null;

// ── Reactive load status ────────────────────────────────────────────────────
// A failed/slow fetch used to be invisible past a single console.warn - the
// panel just looked sparse forever, with no way for a host UI to tell "still
// loading" apart from "there is no library". Mirrors objectRegistry.ts's
// listeners-Set + useSyncExternalStore-compatible getter pattern.
export type LibraryLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

let status: LibraryLoadStatus = 'idle';
let errorMessage: string | null = null;
const statusListeners = new Set<() => void>();

function setStatus(next: LibraryLoadStatus, message: string | null = null): void {
  status = next;
  errorMessage = message;
  for (const listener of statusListeners) listener();
}

/** Subscribe to library load status changes (useSyncExternalStore-compatible). */
export function subscribeLibraryLoadStatus(listener: () => void): () => void {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

export function getLibraryLoadStatus(): LibraryLoadStatus {
  return status;
}

/** Non-null only when status is 'error'. */
export function getLibraryLoadError(): string | null {
  return errorMessage;
}

/**
 * Fetch `<base>/manifest.json` and register every model in it as a placeable
 * object. Idempotent per session: concurrent callers share one in-flight
 * promise, and a completed load short-circuits.
 *
 * @param base - Library root, with no trailing slash. Production hosts pass the
 *   uploads CDN's `/library` path; dev servers can serve it same-origin.
 */
export function ensureLibraryLoaded(base: string): Promise<void> {
  if (isLibraryRegistered()) return Promise.resolve();
  // A second call with a different base while the first is in flight would
  // register the wrong URLs against the manifest that wins; first base wins,
  // matching registerLibraryPack's own rule.
  if (pending && loadedBase === base) return pending;
  loadedBase = base;
  lastAttemptedBase = base;
  setStatus('loading');
  pending = fetch(`${base}/manifest.json`)
    .then(async (response) => {
      if (!response.ok) throw new Error(`manifest fetch ${response.status}`);
      // A dev server with no asset-library build mounted still answers
      // every route with 200 (its SPA index.html fallback), so a missing
      // manifest surfaces here as a JSON parse failure rather than a
      // fetch error - worth a clearer message than the raw SyntaxError,
      // since it's the single most common way this ever fails locally.
      const contentType = response.headers.get('content-type') ?? '';
      if (!contentType.includes('json')) {
        throw new Error(`no asset-library build is mounted at ${base} (got ${contentType || 'an unexpected response'} instead of JSON)`);
      }
      const manifest = parseLibraryManifest(await response.json());
      registerLibraryPack(manifest, base);
      setStatus('ready');
    })
    .catch((err: unknown) => {
      pending = null;
      loadedBase = null;
      const message = err instanceof Error ? err.message : String(err);
      console.warn('[library] asset library unavailable:', message);
      setStatus('error', message);
    });
  return pending;
}

/**
 * Re-run ensureLibraryLoaded against whichever base the host last tried
 * (set by any prior ensureLibraryLoaded call, success or failure). No-op if
 * nothing has ever attempted a load yet.
 */
export function retryLibraryLoad(): Promise<void> | null {
  if (!lastAttemptedBase) return null;
  return ensureLibraryLoaded(lastAttemptedBase);
}
